import React from 'react';
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "balance-of-payments-current-capital-account-india",
  title: "Balance of Payments Explained: Current Account, Capital Account, BOP Crisis & India's Data",
  description: "Understand the Balance of Payments, its components, India's historical data, and the 1991 crisis. Complete notes with solved numericals.",
  pubDate: "2025-08-21",
  authorSlug: "dhairya-madaan",
  category: "subject-explainers",
  tags: ["Macroeconomics", "International Trade", "BOP", "Indian Economy"],
  readMinutes: 14,
  heroEyebrow: "Economics Notes"
};

export const Body = () => {
  return (
    <article>
      <h2>Introduction to Balance of Payments (BOP)</h2>
      <p>The Balance of Payments (BOP) is a systematic statistical record of all economic transactions between the residents of a reporting country and the residents of the rest of the world over a specified period, usually a year. It serves as a vital economic indicator of a nation's global economic standing.</p>

      <h2>BOP Structure</h2>
      <h3>The Current Account</h3>
      <p>The current account records transactions related to trade in goods and services, and current transfers.</p>
      <ul>
        <li><strong>Trade Balance:</strong> Exports minus imports of visible goods.</li>
        <li><strong>Invisibles:</strong> Trade in services (IT, tourism, shipping).</li>
        <li><strong>Income:</strong> Factor incomes like interest, profits, and dividends.</li>
        <li><strong>Transfers:</strong> Unilateral transfers like worker remittances and foreign aid.</li>
      </ul>

      <h3>The Capital Account</h3>
      <p>The capital account records capital transfers and acquisition/disposal of non-produced, non-financial assets. More broadly in financial terms, it tracks capital flows:</p>
      <ul>
        <li><strong>Foreign Direct Investment (FDI):</strong> Long-term investments in physical assets or significant business ownership.</li>
        <li><strong>Foreign Portfolio Investment (FPI):</strong> Short-term investments in financial assets like stocks and bonds.</li>
        <li><strong>External Commercial Borrowings (ECB):</strong> Loans raised by corporations in foreign currency.</li>
        <li><strong>NRI Deposits:</strong> Deposits in Indian banks by Non-Resident Indians.</li>
      </ul>

      <h2>Double-Entry Bookkeeping in BOP</h2>
      <p>BOP always balances in an accounting sense because it uses a double-entry bookkeeping system. Every international transaction results in equal and opposite credit and debit entries. Credits are inflows of forex (exports), while debits are outflows (imports).</p>
      <p>Autonomous vs Accommodating Transactions: Autonomous transactions are undertaken for their own sake (e.g., profit motive), occurring "above the line". Accommodating transactions are undertaken by the central bank to bridge the gap in the BOP, occurring "below the line".</p>

      <h2>India's BOP Crisis of 1991</h2>
      <p>In 1991, India faced a severe macroeconomic crisis. Causes included heavy government borrowing, high current account deficit, the Gulf War shock which spiked oil prices and reduced remittances, and political instability. Forex reserves plummeted to just enough to cover 3 weeks of imports. The resolution involved approaching the IMF, devaluing the Rupee, and undertaking historic Liberalization, Privatization, and Globalization (LPG) reforms.</p>

      <h2>Advanced Concepts</h2>
      <h3>Twin Deficit Hypothesis</h3>
      <p>This hypothesis posits a strong causal link between a nation's government budget deficit and its current account deficit. High fiscal deficits can increase domestic demand, spilling over into higher imports and widening the CAD.</p>
      
      <h3>Marshall-Lerner Condition</h3>
      <p>This condition states that a currency devaluation will only improve the balance of trade if the absolute sum of the long-term export and import demand elasticities is greater than one.</p>
      
      <h3>J-Curve Effect</h3>
      <p>Following a devaluation, a country's trade balance may initially worsen before improving. This creates a "J" shape over time because in the short run, trade contracts are fixed, making imports instantly more expensive, while export volumes take time to respond to lower prices.</p>

      <h2>India's BOP Data (Recent Trends)</h2>
      <table>
        <thead>
          <tr>
            <th>Year</th>
            <th>Current Account (% of GDP)</th>
            <th>Forex Reserves ($ Billion)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>2019-20</td>
            <td>-0.9%</td>
            <td>477</td>
          </tr>
          <tr>
            <td>2020-21</td>
            <td>+0.9% (Surplus)</td>
            <td>576</td>
          </tr>
          <tr>
            <td>2021-22</td>
            <td>-1.2%</td>
            <td>607</td>
          </tr>
          <tr>
            <td>2022-23</td>
            <td>-2.0%</td>
            <td>578</td>
          </tr>
        </tbody>
      </table>

      <h2>Solved Numerical Problems</h2>
      <h3>Problem 1: Constructing BOP</h3>
      <p>Given: Exports = 500, Imports = 700, Net Services = 100, Net Remittances = 50, Net FDI = 150, Net FPI = -50.</p>
      <p>Calculate Current Account Balance (CAB) and Capital Account Balance (KAB).</p>
      <p>Solution:</p>
      <p>Trade Balance = 500 - 700 = -200.</p>
      <p>CAB = Trade Balance + Net Services + Net Remittances = -200 + 100 + 50 = -50.</p>
      <p>KAB = Net FDI + Net FPI = 150 + (-50) = 100.</p>
      <p>Overall Balance = CAB + KAB = -50 + 100 = +50. Forex reserves increase by 50.</p>

    </article>
  );
};


